import type { UseDatadog } from '~~/layers/base/types/datadog'

/** Profile ids already reported this session — avoid spamming RUM on every refetch. */
const reportedMissingNameProfileIds = new Set<string>()

/** Clears dedupe state (e.g. on logout). */
export function resetReportedMissingProfileNameFields(): void {
    reportedMissingNameProfileIds.clear()
}

type ProfileNameRow = {
    id: string
    firstName: string | null
    lastName: string | null
}

/**
 * Logs to Datadog when a signed-in profile row is missing `first_name` and/or `last_name`.
 * Non-fatal: callers continue rendering with empty strings. Reports at most once per profile id
 * per browser session.
 */
export function reportMissingProfileNameFields(
    profile: ProfileNameRow,
    datadog: Pick<UseDatadog, 'logError'>
): void {
    if (!import.meta.client) {
        return
    }

    const missingFirstName = !(profile.firstName?.trim() ?? '')
    const missingLastName = !(profile.lastName?.trim() ?? '')
    if (!missingFirstName && !missingLastName) {
        reportedMissingNameProfileIds.delete(profile.id)
        return
    }
    if (reportedMissingNameProfileIds.has(profile.id)) {
        return
    }

    reportedMissingNameProfileIds.add(profile.id)
    datadog.logError(new Error('Profile missing required name fields'), {
        feature: 'profile',
        action: 'missing_name_fields',
        profileId: profile.id,
        missingFirstName,
        missingLastName,
    })
}
